// The builder's fog (item 11; Marko approved the mockups 2026-11-04: "you can start samurai and
// fog builder"). The starting light, how the fog clears and the key cells, read from and written
// to the document's fog. "Around a correct digit" is SudokuPad's plain fog (no triggers) as long
// as there are no key cells; everything else is dynamic fog, one trigger per cell first, in cell
// order (its 3×3 without the held-back cells, or its own cell), then the key cells and groups.
import type { FogSpec, FogTrigger, PuzzleDoc, Rc } from '@zinc/shared';

export type Reveal = 'around' | 'held' | 'own' | 'groups';

/** A key cell (or a group: several `when` cells, all correct) and the cells it lights. */
export interface FogKey {
  when: Rc[];
  light: Rc[];
}

export interface FogModel {
  visible: Rc[];
  reveal: Reveal;
  /** Cells the 3×3 around a digit leaves in the fog ("held"). */
  held: Rc[];
  keys: FogKey[];
}

export const REVEALS: { key: Reveal; label: string }[] = [
  { key: 'around', label: 'Around a correct digit (the 3×3)' },
  { key: 'held', label: 'Around a correct digit, some cells held back' },
  { key: 'own', label: 'Its own cell; key cells light areas' },
  { key: 'groups', label: 'Groups: a finished group lights an area' },
];

const sameRc = (a: Rc, b: Rc) => a[0] === b[0] && a[1] === b[1];
export const hasRc = (list: Rc[], rc: Rc) => list.some((x) => sameRc(x, rc));
const sameSet = (a: Rc[], b: Rc[]) => a.length === b.length && a.every((x) => hasRc(b, x));

/** The grid's cells, in the core's order. */
export function gridCells(doc: PuzzleDoc): Rc[] {
  if (doc.grid.cells) return doc.grid.cells;
  const out: Rc[] = [];
  for (let r = 0; r < doc.grid.rows; r++) for (let c = 0; c < doc.grid.cols; c++) out.push([r, c]);
  return out;
}

function around(cells: Rc[], rc: Rc): Rc[] {
  return cells.filter((x) => Math.abs(x[0] - rc[0]) <= 1 && Math.abs(x[1] - rc[1]) <= 1);
}

/** The draft's fog as the builder edits it; null without fog. */
export function fogModel(doc: PuzzleDoc): FogModel | null {
  const f = doc.fog;
  if (!f) return null;
  const visible = f.visible ?? [];
  const triggers = f.triggers ?? [];
  const keysOf = (list: FogTrigger[]) => list.map((t) => ({ when: t.when, light: t.light }));
  if (triggers.length === 0) return { visible, reveal: 'around', held: [], keys: [] };
  const cells = gridCells(doc);
  const head = triggers.slice(0, cells.length);
  const perCell =
    head.length === cells.length &&
    head.every((t, i) => t.when.length === 1 && sameRc(t.when[0]!, cells[i]!));
  // Dynamic fog the builder didn't write (an imported puzzle): every trigger a key.
  if (!perCell) return { visible, reveal: 'groups', held: [], keys: keysOf(triggers) };
  const keys = keysOf(triggers.slice(cells.length));
  if (head.every((t, i) => t.light.length === 1 && sameRc(t.light[0]!, cells[i]!)))
    return { visible, reveal: 'own', held: [], keys };
  const held = cells.filter((c) => !head.some((t) => hasRc(t.light, c)));
  const fits = head.every((t, i) =>
    sameSet(
      t.light,
      around(cells, cells[i]!).filter((x) => !hasRc(held, x)),
    ),
  );
  if (!fits) return { visible, reveal: 'groups', held: [], keys: keysOf(triggers) };
  return { visible, reveal: held.length ? 'held' : 'around', held, keys };
}

/** The document with this fog (or none). */
export function withFog(doc: PuzzleDoc, m: FogModel | null): PuzzleDoc {
  if (!m) return { ...doc, fog: null };
  const cells = gridCells(doc);
  const keys = m.keys
    .filter((k) => k.when.length > 0 && k.light.length > 0)
    .map((k) => ({ when: k.when, light: k.light }));
  let triggers: FogTrigger[];
  if (m.reveal === 'around' && keys.length === 0) {
    triggers = [];
  } else if (m.reveal === 'around' || m.reveal === 'held') {
    const held = m.reveal === 'held' ? m.held : [];
    triggers = [
      ...cells.map((c) => ({ when: [c], light: around(cells, c).filter((x) => !hasRc(held, x)) })),
      ...keys,
    ];
  } else if (m.reveal === 'own') {
    triggers = [...cells.map((c) => ({ when: [c], light: [c] })), ...keys];
  } else {
    triggers = keys;
  }
  const fog: FogSpec = { ...(doc.fog ?? {}), visible: m.visible, reveal: 'sudokupad' };
  if (triggers.length) fog.triggers = triggers;
  else delete fog.triggers;
  return { ...doc, fog };
}

/** Fog switched on: nothing lit yet, the 3×3 around a correct digit. */
export const NEW_FOG: FogModel = { visible: [], reveal: 'around', held: [], keys: [] };

/** Adds or takes away cells (the Light and Hold back tools). */
export function paintCells(list: Rc[], cells: Rc[], on: boolean): Rc[] {
  if (on) return [...list, ...cells.filter((c) => !hasRc(list, c))];
  return list.filter((c) => !hasRc(cells, c));
}

/** "r5c5 lights 8 cells" or "Group r7c1–r7c3 lights 9 cells". */
export function describeKey(k: FogKey): string {
  const name = (rc: Rc) => `r${rc[0] + 1}c${rc[1] + 1}`;
  const lights = `lights ${k.light.length} cell${k.light.length === 1 ? '' : 's'}`;
  if (k.when.length === 1) return `${name(k.when[0]!)} ${lights}`;
  const first = k.when[0]!;
  const last = k.when[k.when.length - 1]!;
  return `Group ${name(first)}–${name(last)}, all correct, ${lights}`;
}
